import { useQuery } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";
import { DELIVERY_TAB_STATUS_FILTER, DELIVERY_TABS } from "../../../components/delivery/constants";

// GET /delivery — the SAME endpoint the legacy /delivery page uses
// (src/hooks/fetch-hooks/get-all-deliveries/fetch-deliveries.js). The logistics console is a
// restyle, so this hook must send the same request that page sends; the only addition is
// `isCod`, which COD Watch passes (spec §7).
//
// The tab is never sent as-is: DELIVERY_TAB_STATUS_FILTER translates it into the comma-joined
// `status` list the API expects. Reused from the legacy constants rather than re-declared, so
// the two pages cannot drift apart on what "New" means.
//
// Search is sent as BOTH patientName and userName, matching the legacy hook. Delivery.Get
// only reads userName (classes/Delivery/Delivery.js:88) and ignores patientName — kept
// identical deliberately, so search behaves the same on both pages.
export default function useDeliveries({
    page = 1,
    size = 50,
    search = "",
    tab = DELIVERY_TABS.NEW,
    isCod = false,
    deliveryPartnerId = "",
    enabled = true,
} = {}) {
    const status = DELIVERY_TAB_STATUS_FILTER[tab] || DELIVERY_TAB_STATUS_FILTER[DELIVERY_TABS.NEW];
    const trimmed = (search || "").trim();

    const params = {
        page,
        size,
        status,
        ...(trimmed ? { patientName: trimmed, userName: trimmed } : {}),
        ...(isCod ? { isCod: true } : {}),
        // An exact match on deliveries.deliveryPartnerId -- it is in the delivery
        // GetAttributes, so the generic where-builder handles it with no API change.
        ...(deliveryPartnerId ? { deliveryPartnerId } : {}),
    };

    return useQuery(
        ["logistics-deliveries", params],
        async () => {
            const result = await request({ url: "delivery", method: "GET", params });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;

            const { count, rows } = body.data || {};
            return {
                deliveries: rows || [],
                pagination: { page, size, total: Number(count) || 0 },
            };
        },
        { keepPreviousData: true, retry: false, enabled }
    );
}
